import { CodeableConcept, Condition, Extension, Patient } from "@medplum/fhirtypes";
import { ISO_DATE } from "@metriport/shared/common/date";
import { ColDef } from "ag-grid-community";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { GenerateTableDataParams } from "..";
import { MrFilterSetting } from "../../../../../api/settings";
import { EhrName } from "../../../../../domain/ehr";
import {
  ICD_10_CODE,
  SNOMED_CODE,
  compare,
  createEhrActionColumnDef,
  filterByDate,
  getConditionStatus,
  getFirstCodeSpecified,
  getResourcesFromBundle,
  getValidCode,
} from "../shared";
import { ConditionCellRenderer } from "./condition-cell";

dayjs.extend(utc);

const CHRONICITY_EXTENSION_URL = "http://hl7.org/fhir/StructureDefinition/condition-related";
const HCC_EXTENSION_URL = `https://public.metriport.com/fhir/StructureDefinition/condition-hcc.json`;
const CONDITION_CATEGORY_SYSTEM = "http://terminology.hl7.org/CodeSystem/condition-category";

export type ConditionRowData = {
  id: string;
  condition: string;
  category: string;
  code: string;
  firstSeen: string;
  lastSeen: string;
  originalData: Condition;
  ehrAction?: string;
  isChronic: boolean;
  isHcc: boolean;
};

type ConditionOccurrence = {
  rawCondition: Condition;
  start: string | undefined;
  end: string | undefined;
  status: string | undefined;
};

export type GroupedConditions = {
  title: string;
  mostRecentCondition: Condition;
  firstCondition: Condition;
  sortedOccurrences?: ConditionOccurrence[];
  status?: string | undefined;
};

export const conditionTableData = ({
  bundle,
  tableFilters,
  ehrActions,
}: GenerateTableDataParams) => {
  const columnDefs: ColDef<ConditionRowData>[] = [
    { field: "id", hide: true },
    {
      field: "condition",
      cellRenderer: ConditionCellRenderer,
    },
    { field: "category" },
    { field: "code" },
    { field: "firstSeen" },
    { field: "lastSeen", sort: tableFilters?.stringFilter ? undefined : "desc" },
    { field: "originalData", hide: true },
  ];

  if (ehrActions && ehrActions.write) {
    const columnDef = createEhrActionColumnDef<Condition, ConditionRowData>({
      ehrActions,
      path: "condition",
      filter: (originalData: Condition) => isShowWriteBackButton(ehrActions.name, originalData),
    });
    columnDefs.push(columnDef);
  }

  const patient = getResourcesFromBundle<Patient>(bundle, "Patient")[0];
  const patientDob = patient?.birthDate ? dayjs.utc(patient.birthDate).format(ISO_DATE) : undefined;

  const conditions = getResourcesFromBundle<Condition>(bundle, "Condition");
  const groupedConditions = groupConditions(conditions, patientDob);
  const fullRowData = getConditionRowData({
    conditions: groupedConditions,
    tableFilters,
    patientDob,
  });

  return {
    columnDefs,
    rowData: fullRowData,
  };
};

export function groupConditions(
  conditions: Condition[],
  patientDob: string | undefined
): GroupedConditions[] {
  const results: GroupedConditions[] = [];
  const conditionMap: { [k: string]: ConditionOccurrence[] } = {};
  conditions.map(c => {
    let title: string;
    const codings = getValidCode(c.code?.coding);
    const displays = codings.map(coding => coding.display);
    const text = c.code?.text;
    if (displays.length) {
      title = Array.from(new Set(displays)).join(", ");
    } else if (text) {
      title = text;
    } else {
      results.push({ title: "-", mostRecentCondition: c, firstCondition: c });
      return;
    }
    if (!c.onsetPeriod && !c.onsetDateTime) {
      results.push({ title, mostRecentCondition: c, firstCondition: c });
      return;
    }

    const conditionPoint = {
      rawCondition: c,
      start: getOnsetTime(c, patientDob),
      end: getEndTime(c, patientDob),
      status: getStatus(c),
    };
    const groupedCondition = conditionMap[title];
    if (groupedCondition) {
      groupedCondition.push(conditionPoint);
    } else {
      conditionMap[title] = [conditionPoint];
    }
  });

  Object.entries(conditionMap).map(([title, values]) => {
    const sortedOccurrences = values.sort((a, b) => {
      const dateA = a.start ? new Date(a.start).getTime() : 0;
      const dateB = b.start ? new Date(b.start).getTime() : 0;

      return dateB - dateA;
    });
    const mostRecent = sortedOccurrences[0];
    if (!mostRecent) return;
    const firstCondition = sortedOccurrences[sortedOccurrences.length - 1];
    if (!firstCondition) return;
    results.push({
      title,
      mostRecentCondition: mostRecent.rawCondition,
      firstCondition: firstCondition.rawCondition,
      sortedOccurrences,
      status: mostRecent.status,
    });
  });
  return results;
}

export function getStatus(condition: Condition): string | undefined {
  return condition.clinicalStatus?.text ??
    condition.clinicalStatus?.coding?.[0]?.display ??
    condition.clinicalStatus?.coding?.[0]?.code === "55561003"
    ? "Active"
    : condition.clinicalStatus?.coding?.[0]?.code;
}

function getConditionRowData({
  conditions,
  tableFilters,
  patientDob,
}: {
  conditions: GroupedConditions[];
  tableFilters: MrFilterSetting | undefined;
  patientDob: string | undefined;
}): ConditionRowData[] {
  return conditions
    .map(condition => {
      const mostRecentCondition = condition.mostRecentCondition;
      const firstCondition = condition.firstCondition;

      const hccCode = getConditionHccCode(mostRecentCondition);

      return {
        id: mostRecentCondition.id ?? "-",
        condition: condition.title,
        category: getConditionCategory(mostRecentCondition),
        code: getConditionCode(mostRecentCondition) + hccCode,
        firstSeen: getEarliestSeen(condition.sortedOccurrences, patientDob),
        lastSeen: getLatestSeen(condition.sortedOccurrences, patientDob),
        originalData: firstCondition,
        isChronic: isChronicCondition(mostRecentCondition),
        isHcc: isHccCondition(mostRecentCondition, hccCode),
      };
    })
    .filter(row => filterByDate(row.firstSeen, tableFilters?.dateFilter))
    .sort((a, b) => {
      if (tableFilters?.stringFilter) {
        return compare(
          {
            ...a,
            originalData: undefined,
            isChronic: undefined,
            isHcc: undefined,
            id: undefined,
          },
          {
            ...b,
            originalData: undefined,
            isChronic: undefined,
            isHcc: undefined,
            id: undefined,
          },
          tableFilters.stringFilter
        );
      }
      return compare(
        {
          firstSeen: a.firstSeen,
          lastSeen: a.lastSeen,
        },
        {
          firstSeen: b.firstSeen,
          lastSeen: b.lastSeen,
        },
        tableFilters?.stringFilter
      );
    });
}

export function getConditionCode(condition: Condition): string {
  const coding = getFirstCodeSpecified(condition.code?.coding ?? [], [ICD_10_CODE, SNOMED_CODE]);

  return coding ? `${coding.system}: ${coding.code}` : "-";
}

export function getOnsetTime(
  condition: Condition,
  patientDob?: string | undefined
): string | undefined {
  const onsetDateTime = condition.onsetDateTime;
  const onsetPeriodStart = condition.onsetPeriod?.start;
  const onsetPeriodEnd = condition.onsetPeriod?.end;

  const time = onsetDateTime || onsetPeriodStart || onsetPeriodEnd;

  if (!time) return undefined;

  const date = dayjs.utc(time).format(ISO_DATE);
  if (patientDob && (date === patientDob || patientDob === "Invalid Date")) return undefined;

  return date;
}

export function getEndTime(
  condition: Condition,
  patientDob?: string | undefined
): string | undefined {
  const onsetDateTime = condition.onsetDateTime;
  const onsetPeriodStart = condition.onsetPeriod?.start;
  const onsetPeriodEnd = condition.onsetPeriod?.end;
  const abatementDateTime = condition.abatementDateTime;
  const abatementPeriodStart = condition.abatementPeriod?.start;
  const abatementPeriodEnd = condition.abatementPeriod?.end;

  const time =
    abatementPeriodEnd ||
    abatementDateTime ||
    abatementPeriodStart ||
    onsetPeriodEnd ||
    onsetDateTime ||
    onsetPeriodStart;

  if (!time) return undefined;

  const date = dayjs.utc(time).format(ISO_DATE);
  if (patientDob && (date === patientDob || patientDob === "Invalid Date")) return undefined;

  return date;
}

function getEarliestSeen(
  occurrences: ConditionOccurrence[] | undefined,
  patientDob: string | undefined
): string {
  const earliest = occurrences?.reduce((acc, curr) => {
    if (!acc || !acc.start || (curr.start && new Date(curr.start) < new Date(acc.start))) {
      if (curr.start && curr.start != patientDob) {
        return curr;
      }
    }
    return acc;
  }, null as ConditionOccurrence | null);

  return earliest?.start ?? "-";
}

function getLatestSeen(
  occurrences: ConditionOccurrence[] | undefined,
  patientDob: string | undefined
): string {
  const latest = occurrences?.reduce((acc, curr) => {
    if (!acc || !acc.end || (curr.end && new Date(curr.end) > new Date(acc.end))) {
      if (curr.end && curr.end != patientDob) {
        return curr;
      }
    }
    return acc;
  }, null as ConditionOccurrence | null);

  return latest?.end ?? "-";
}

function isShowWriteBackButton(ehr: string, condition: Condition): boolean {
  if (ehr === EhrName.Elation || ehr === EhrName.Athena) {
    const snomedCoding = getFirstCodeSpecified(condition.code?.coding ?? [], [SNOMED_CODE]);
    if (!snomedCoding) return false;
  } else if (ehr === EhrName.Canvas || ehr === EhrName.Healthie) {
    const icd10Coding = getFirstCodeSpecified(condition.code?.coding ?? [], [ICD_10_CODE]);
    if (!icd10Coding) return false;
    if (ehr === EhrName.Healthie) return true;
    if (!icd10Coding.code) return false;
    if (!icd10Coding.display) return false;
  }
  const startDate = condition.onsetDateTime ?? condition.onsetPeriod?.start;
  if (!startDate) return false;
  if (ehr === EhrName.Elation || ehr === EhrName.Canvas) {
    const status = getConditionStatus(condition);
    if (!status) return false;
  }
  return true;
}

export function isChronicCondition(condition?: Condition): boolean {
  if (!condition) return false;
  const chronicityExtension = condition.extension?.find(
    (e: Extension) => e.url === CHRONICITY_EXTENSION_URL
  );
  return chronicityExtension?.valueCoding?.code === "C" ? true : false;
}

function isHccExtension(extension: Extension): boolean {
  return extension.url === HCC_EXTENSION_URL;
}

function createHccExtension(
  url: string,
  system: string | undefined,
  display: string,
  version: string,
  code: string
): Extension {
  return {
    url,
    valueCodeableConcept: { coding: [{ system, display, version, code }] },
  };
}

function buildUniqueHccExtention(extensions: Extension[]): Extension[] {
  const hccExtensions = extensions.filter(isHccExtension);
  if (!hccExtensions?.length) return [];
  // Group extensions by display name
  const groupedByDisplay = new Map<string, Extension[]>();
  hccExtensions.forEach(extension => {
    const coding = extension.valueCodeableConcept?.coding?.[0];
    if (!coding?.display) return;

    if (!groupedByDisplay.has(coding.display)) {
      groupedByDisplay.set(coding.display, []);
    }
    groupedByDisplay.get(coding.display)?.push(extension);
  });

  // Combine extensions with same display name
  const result: Extension[] = [];
  groupedByDisplay.forEach((extensionsGroup, display) => {
    if (extensionsGroup.length === 1) {
      // Single extension, ensure it has the consolidated format
      const extension = extensionsGroup[0];
      if (!extension || !extension.url) return;

      const coding = extension.valueCodeableConcept?.coding?.[0];
      if (coding?.code && coding?.version) {
        const consolidatedExtension = createHccExtension(
          extension.url,
          coding.system,
          display,
          coding.version,
          `${coding.code} (${coding.version})`
        );
        result.push(consolidatedExtension);
      } else {
        // Fallback to original if missing version info
        result.push(extension);
      }
    } else {
      // Multiple extensions with same display, combine them
      const firstExtension = extensionsGroup[0];
      if (!firstExtension || !firstExtension.url) return;

      const allCodings = extensionsGroup
        .map(ext => ext.valueCodeableConcept?.coding?.[0])
        .filter((coding): coding is NonNullable<typeof coding> => !!coding);

      // Combine versions and codes
      const versions = allCodings.map(c => c.version).filter((v): v is string => !!v);
      const codes = allCodings
        .map(c => (c.code && c.version ? `${c.code} (${c.version})` : ""))
        .filter(Boolean);

      const combinedExtension = createHccExtension(
        firstExtension.url,
        firstExtension.valueCodeableConcept?.coding?.[0]?.system,
        display,
        versions.join(", "),
        codes.join(", ")
      );

      result.push(combinedExtension);
    }
  });

  return result;
}

export function getConditionHccCode(condition?: Condition): string {
  if (!condition?.extension?.length) return "";
  const consolidatedExtensions = buildUniqueHccExtention(condition.extension);
  if (!consolidatedExtensions.length) return "";
  const result = consolidatedExtensions
    .map(e => {
      const coding = e.valueCodeableConcept?.coding?.[0];
      return coding?.code ? coding.code : "";
    })
    .filter(Boolean)
    .join(", ");

  return result !== "" ? `; HCC: ${result}` : "";
}

export function isHccCondition(condition?: Condition, hccCode?: string): boolean {
  return !!condition?.extension?.find(isHccExtension) && !!hccCode?.includes("v28"); // is HCC if includes v28!
}

export function getHccCoditionsForSidePanel(condition?: Condition): CodeableConcept[] {
  if (!condition?.extension?.length) return [];
  const consolidatedExtensions = buildUniqueHccExtention(condition.extension);
  return consolidatedExtensions
    .map(e => e.valueCodeableConcept)
    .filter(Boolean) as CodeableConcept[];
}

export function getConditionCategory(condition?: Condition): string {
  if (!condition?.category?.length) return "-";
  for (const category of condition.category) {
    if (!category.coding?.length) continue;
    const withCategory = category.coding.find(c => c.system === CONDITION_CATEGORY_SYSTEM);
    if (withCategory?.display) return withCategory.display;
  }
  return "-";
}
